import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usesAcademyWordmark } from '@/shared/wordmark';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { Mark } from '../../components/Mark';
import { Panel } from '../../components/Panel';
import { ProblemNotice } from '../../components/ProblemNotice';
import { PublicFrame } from '../../components/Shell';
import { useRequestLink } from '../../lib/queries';
import { useTenant } from '../../lib/tenant-context';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A same-origin path to return to after sign-in; anything else goes home. */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return '/';
  return raw;
}

export default function SignIn() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const request = useRequestLink();
  const codeMode = tenant.enrolment_mode === 'code';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!EMAIL_RE.test(address)) {
      setError('Enter the email address your academy invitation was sent to.');
      return;
    }
    setError(null);
    const body: { email: string; next: string; enrol_code?: string } = { email: address, next };
    if (codeMode && code.trim()) body.enrol_code = code.trim();
    const res = await request.mutateAsync(body).catch(() => null);
    if (!res) return;
    // The link comes back only from the log mailer outside production. It travels in router state,
    // never in the address bar, so it is not written to history or a referrer.
    navigate(`/check-email?email=${encodeURIComponent(address)}`, {
      state: res.link ? { link: res.link } : null,
    });
  };

  return (
    <PublicFrame>
      <Panel as="form" padding="lg" onSubmit={submit} noValidate>
        <div className="mb-5">
          <Mark name={tenant.name} logoUrl={tenant.logo_url} brandMode={tenant.brand_mode} size={34} />
        </div>
        {/* Stacked under the mark, the Academy's name is its Crimson Pro wordmark; an operator's
            name keeps the page title face. */}
        <h1
          className={
            usesAcademyWordmark(tenant) ? 'wordmark-title text-ink' : 't-title rail:text-[27px] text-ink'
          }
        >
          {tenant.name}
        </h1>
        <p className="mb-6 mt-2 text-inkMuted">Sign in with your email address.</p>
        <div className="flex flex-col gap-4">
          <Field
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error}
            required
          />
          {codeMode && (
            <Field
              label="Enrolment code"
              type="text"
              name="enrol_code"
              autoComplete="off"
              autoCapitalize="characters"
              placeholder="From your invitation"
              hint={`Needed the first time you sign in with ${tenant.short_name}.`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          )}
          <Button type="submit" block busy={request.isPending}>
            {request.isPending ? 'Sending the link' : 'Send me a sign-in link'}
          </Button>
        </div>
        {request.error && <ProblemNotice error={request.error} compact className="mt-4" />}
      </Panel>
      <Link
        to="/explore"
        className="mt-3 flex min-h-11 items-center justify-center text-[14px] text-accentText underline underline-offset-4"
      >
        About the programme
      </Link>
    </PublicFrame>
  );
}
